import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { arrayOf, shape } from 'prop-types';

import Api from 'core/api';

import Text from 'components/Common/Text';
import Modal from 'components/Common/ModalV2';
import Checkbox from 'components/Common/Checkbox';
import InfoNote from 'components/Common/InfoNote';
import Button from 'components/Common/Buttons/ButtonV3';

import useModal from 'hooks/useModal';
import useMutation from 'hooks/useMutation';

import { ensureArray, addOrRemove, isNonEmptyArray } from 'utils/arrays';

import s from './DownloadSurveyModal.css';

const DownloadSurveyModal = ({ reports }) => {
  const { hideModal } = useModal();
  const [checkedReports, setCheckedReports] = useState([]);
  const [showError, setShowError] = useState(false);

  const {
    id: studentId,
    firstName,
    lastName,
  } = useSelector(({ casefile }) => casefile?.studentDetails);

  const { mutate: downloadSurveyReport } = useMutation({
    apiFunc: Api.swipe.downloadBulkReports,
    options: {
      queryKey: 'Api.swipe.downloadBulkReports',
    },
    successSnackbarMessage:
      'Check your email for the report! Also you will receive a notification shortly. You can access the download link from the bell icon in the upper right-hand corner of the screen.',
    errorSnackbarMessage:
      'There was an issue creating your report. Please, contact a site admin.',
  });

  useEffect(() => {
    if (isNonEmptyArray(checkedReports) && showError) {
      setShowError(false);
    }
  }, [checkedReports, showError]);

  const isAllReportsSelected =
    checkedReports.length === ensureArray(reports).length;

  const onSelectAll = () => {
    const newSelection = isAllReportsSelected
      ? []
      : ensureArray(reports).map(({ value }) => value);
    setCheckedReports(newSelection);
  };

  const downloadReports = () => {
    if (!isNonEmptyArray(checkedReports)) {
      setShowError(true);
    } else {
      hideModal();
      downloadSurveyReport({
        data: {
          students: [studentId],
          type: checkedReports.join(','),
        },
      });
    }
  };

  const modalTitle = `Generate Reports for ${firstName} ${lastName}`;

  return (
    <Modal
      onClose={hideModal}
      title={modalTitle}
      classes={{ paperScrollPaper: s.paperScrollPaper }}
      actions={
        <>
          <Button
            variant="outlined"
            color="gray"
            onClick={hideModal}
            label="Cancel"
          />
          <Button label="Download" variant="filled" onClick={downloadReports} />
        </>
      }
    >
      <div className={s.content}>
        <div className={s.header}>
          <Checkbox
            label="Select All"
            value={isAllReportsSelected}
            checked={isAllReportsSelected}
            onChange={onSelectAll}
            classes={{ root: s.checkboxRoot }}
            checkBoxClasses={{ root: s.checkbox }}
          />
        </div>
        <div className={s.bucket}>
          {ensureArray(reports).map(({ label, value }) => (
            <div key={label} className={s.report}>
              <Checkbox
                label={label}
                checked={checkedReports.includes(value)}
                onChange={() =>
                  setCheckedReports(addOrRemove(checkedReports, value))
                }
                classes={{ root: s.checkboxRoot }}
                checkBoxClasses={{ root: s.checkbox }}
              />
              <Text color="grayDark">PDF</Text>
            </div>
          ))}
        </div>
        {showError ? (
          <InfoNote variant="error" containerClass={s.errorInfo} size="large">
            You must select at least one assessment to download a report.
          </InfoNote>
        ) : null}
      </div>
    </Modal>
  );
};

DownloadSurveyModal.propTypes = {
  reports: arrayOf(shape({})).isRequired,
};

export default DownloadSurveyModal;
